/**
 * Component for displaying a gallery of available golf courses
 */
import React, { useState } from 'react';
import { GolfCourse } from '../types';
import { getAllGolfCourses, getFilteredGolfCourses } from '../services/golfCourseService';
import '../styles/GolfCourseGallery.css';

/**
 * Filter options for golf courses
 */
interface FilterOptions {
  difficulty: GolfCourse['difficulty'][];
  priceRange: { min: number; max: number };
  holeCount: number | null;
  amenities: string[];
}

/**
 * Props for the GolfCourseGallery component
 */
interface GolfCourseGalleryProps {
  onSelectCourse: (course: GolfCourse) => void;
  selectedCourseId?: string;
}

/**
 * GolfCourseGallery component - displays available golf courses in a visually appealing grid
 * @param props Component props
 * @returns React component
 */
const GolfCourseGallery: React.FC<GolfCourseGalleryProps> = ({ onSelectCourse, selectedCourseId }) => {
  const [courses, setCourses] = useState<GolfCourse[]>(getAllGolfCourses());
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    difficulty: [],
    priceRange: { min: 0, max: 600 },
    holeCount: null,
    amenities: [],
  });

  // All possible amenities across courses
  const allAmenities = Array.from(
    new Set(
      getAllGolfCourses().flatMap(course => course.amenities)
    )
  ).sort();

  /**
   * Handles difficulty filter changes
   * @param difficulty Difficulty to toggle
   */
  const handleDifficultyChange = (difficulty: GolfCourse['difficulty']) => {
    setFilterOptions(prev => {
      const newDifficulties = prev.difficulty.includes(difficulty)
        ? prev.difficulty.filter(d => d !== difficulty)
        : [...prev.difficulty, difficulty];
      
      return {
        ...prev,
        difficulty: newDifficulties,
      };
    });
  };

  /**
   * Handles hole count filter changes
   * @param count Hole count to set
   */
  const handleHoleCountChange = (count: number | null) => {
    setFilterOptions(prev => ({
      ...prev,
      holeCount: count,
    }));
  };

  /**
   * Handles amenity filter changes
   * @param amenity Amenity to toggle
   */
  const handleAmenityChange = (amenity: string) => {
    setFilterOptions(prev => {
      const newAmenities = prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity];
      
      return {
        ...prev,
        amenities: newAmenities,
      };
    });
  };

  /**
   * Applies the current filters to golf courses
   */
  const applyFilters = () => {
    const filteredCourses = getFilteredGolfCourses({
      difficulty: filterOptions.difficulty.length > 0 ? filterOptions.difficulty : undefined,
      priceRange: filterOptions.priceRange,
      holeCount: filterOptions.holeCount || undefined,
      amenities: filterOptions.amenities.length > 0 ? filterOptions.amenities : undefined,
    });
    setCourses(filteredCourses);
    setShowFilters(false);
  };

  /**
   * Resets all filters
   */
  const resetFilters = () => {
    setFilterOptions({
      difficulty: [],
      priceRange: { min: 0, max: 600 },
      holeCount: null,
      amenities: [],
    });
    setCourses(getAllGolfCourses());
    setShowFilters(false);
  };

  /**
   * Formats price range for display
   * @param range Price range object
   * @returns Formatted price range string
   */
  const formatPriceRange = (range: { min: number; max: number }) => {
    return `$${range.min} - $${range.max}`;
  };

  return (
    <div className="golf-course-gallery">
      <div className="gallery-header">
        <h2>Select a Golf Course</h2>
        <button 
          className="filter-toggle-button" 
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {showFilters && (
        <div className="course-filters">
          <div className="filter-section">
            <h3>Difficulty</h3>
            <div className="filter-options">
              {['Beginner', 'Intermediate', 'Advanced', 'Pro'].map(diff => (
                <label key={diff} className="filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={filterOptions.difficulty.includes(diff as GolfCourse['difficulty'])}
                    onChange={() => handleDifficultyChange(diff as GolfCourse['difficulty'])}
                  />
                  {diff}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <h3>Holes</h3>
            <div className="filter-options">
              <label className="filter-radio">
                <input 
                  type="radio" 
                  name="holeCount" 
                  checked={filterOptions.holeCount === null}
                  onChange={() => handleHoleCountChange(null)}
                />
                Any
              </label>
              <label className="filter-radio">
                <input 
                  type="radio" 
                  name="holeCount" 
                  checked={filterOptions.holeCount === 9}
                  onChange={() => handleHoleCountChange(9)}
                />
                9 Holes
              </label>
              <label className="filter-radio">
                <input 
                  type="radio" 
                  name="holeCount" 
                  checked={filterOptions.holeCount === 18}
                  onChange={() => handleHoleCountChange(18)}
                />
                18 Holes
              </label>
            </div>
          </div>

          <div className="filter-section">
            <h3>Amenities</h3>
            <div className="filter-options amenities-grid">
              {allAmenities.map(amenity => (
                <label key={amenity} className="filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={filterOptions.amenities.includes(amenity)}
                    onChange={() => handleAmenityChange(amenity)}
                  />
                  {amenity}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-actions">
            <button className="filter-apply-button" onClick={applyFilters}>Apply Filters</button>
            <button className="filter-reset-button" onClick={resetFilters}>Reset</button>
          </div>
        </div>
      )}

      <div className="courses-grid">
        {courses.map(course => (
          <div 
            key={course.id} 
            className={`course-card ${selectedCourseId === course.id ? 'selected' : ''}`}
            onClick={() => onSelectCourse(course)}
          >
            <div className="course-image-container">
              <img 
                src={course.imageUrl} 
                alt={course.name} 
                className="course-image" 
              />
              <div className="course-difficulty">{course.difficulty}</div>
              <div className="course-rating">
                <span className="rating-stars">
                  {Array(5).fill('★').map((star, i) => (
                    <span 
                      key={i} 
                      className={i < Math.floor(course.rating) ? 'filled' : ''}
                    >
                      {star}
                    </span>
                  ))}
                </span>
                <span className="rating-value">{course.rating.toFixed(1)}</span>
              </div>
            </div>
            <div className="course-details">
              <h3 className="course-name">{course.name}</h3>
              <p className="course-location">{course.location}</p>
              <p className="course-info">
                <span>{course.holeCount} holes</span>
                <span className="price-range">
                  {formatPriceRange(course.pricingRange.weekday)}
                </span>
              </p>
              <div className="course-amenities">
                {course.amenities.slice(0, 3).map(amenity => (
                  <span key={amenity} className="amenity-tag">{amenity}</span>
                ))}
                {course.amenities.length > 3 && (
                  <span className="amenity-tag">+{course.amenities.length - 3} more</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GolfCourseGallery;
